import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminAuth } from "@/lib/adminAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Lock, Mail, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";

const AdminLogin = () => {
  const nav = useNavigate();
  const { login } = useAdminAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return toast.error("Enter your admin email and password");
    setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Signed in");
      nav("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-slate-950 grid place-items-center px-4">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center text-center mb-6 text-white">
          <Logo size={56} variant="light"/>
          <div className="mt-3">
            <div className="font-display font-bold">CarryIt</div>
            <div className="text-xs text-slate-400 -mt-0.5">Founder back office</div>
          </div>
        </div>
        <div className="rounded-2xl bg-white p-6 shadow-xl">
          <div className="flex items-center gap-2 mb-4">
            <ShieldAlert className="w-4 h-4 text-amber-600"/>
            <div className="font-display font-bold text-slate-900">Restricted access</div>
          </div>
          <p className="text-xs text-slate-500 mb-5">Only authorised administrators may sign in here. All activity is logged.</p>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500">Email</label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
                <Input
                  data-testid="admin-email-input"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-9 rounded-xl"
                  placeholder="you@carryit.example"
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500">Password</label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
                <Input
                  data-testid="admin-password-input"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-9 rounded-xl"
                  placeholder="••••••••"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={busy}
              data-testid="admin-login-btn"
              className="w-full rounded-full bg-slate-900 hover:bg-slate-800"
            >
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>
          <p className="text-[11px] text-slate-400 mt-5">
            Sessions expire after 8 hours of inactivity. Wrong password five times locks the account for 15 minutes.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
